import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type DockviewApi,
  DockviewReact,
  type DockviewReadyEvent,
  type IDockviewPanelProps,
  type SerializedDockview,
  themeDark,
  themeLight,
} from 'dockview-react';
import 'dockview-react/dist/styles/dockview.css';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { ChartTile } from '../workstation/chart-tile';
import { McpAppTile } from '../workstation/mcp-app-tile';
import {
  AccountTile,
  ChatTile,
  CreditsTile,
  JobsTile,
  OrdersTile,
  TradesTile,
} from '../workstation/tiles';
import { WidgetTile } from '../workstation/widget-tile';

/**
 * D10 workstation: dockview tiles (docked groups, floating groups, popout windows) over the control
 * plane's data. The layout is saved per user (`/v1/workstation/layouts/main`) 800 ms after the last
 * change and restored on load; "Reset layout" returns to the default arrangement.
 */
const LAYOUT = 'main';

type TileKind = 'chart' | 'account' | 'orders' | 'trades' | 'jobs' | 'credits' | 'chat' | 'app';
/** Widgets are tiles too, keyed by widget id (`widget-<id>`); they are not on the Add-tile bar. */
type Tile = FunctionComponent<IDockviewPanelProps>;
const TILES: Record<TileKind, { title: string; component: Tile }> = {
  chart: { title: 'Chart', component: ChartTile as Tile },
  account: { title: 'Account', component: AccountTile as Tile },
  orders: { title: 'Open orders', component: OrdersTile as Tile },
  trades: { title: 'Fills', component: TradesTile as Tile },
  jobs: { title: 'Jobs', component: JobsTile },
  credits: { title: 'Credits', component: CreditsTile },
  chat: { title: 'Agent', component: ChatTile as Tile },
  app: { title: 'App', component: McpAppTile as Tile },
};
const components: Record<string, Tile> = {
  ...Object.fromEntries(Object.entries(TILES).map(([k, v]) => [k, v.component])),
  widget: WidgetTile as Tile,
};

const nextId = (dock: DockviewApi, kind: TileKind) => {
  let i = 1;
  while (dock.getPanel(`${kind}-${i}`)) i++;
  return `${kind}-${i}`;
};

function defaultLayout(dock: DockviewApi) {
  dock.clear();
  const chart = dock.addPanel({
    id: 'chart-1',
    component: 'chart',
    title: 'Chart',
    params: { coin: 'ETH', interval: '1h' },
  });
  const account = dock.addPanel({
    id: 'account-1',
    component: 'account',
    title: 'Account',
    position: { referencePanel: chart, direction: 'right' },
    initialWidth: 460,
  });
  const orders = dock.addPanel({
    id: 'orders-1',
    component: 'orders',
    title: 'Open orders',
    position: { referencePanel: account, direction: 'below' },
  });
  dock.addPanel({
    id: 'trades-1',
    component: 'trades',
    title: 'Fills',
    position: { referencePanel: orders, direction: 'within' },
    inactive: true,
  });
  const chat = dock.addPanel({
    id: 'chat-1',
    component: 'chat',
    title: 'Agent',
    position: { referencePanel: chart, direction: 'below' },
    initialHeight: 320,
  });
  dock.addPanel({
    id: 'jobs-1',
    component: 'jobs',
    title: 'Jobs',
    position: { referencePanel: chat, direction: 'within' },
    inactive: true,
  });
  dock.addPanel({
    id: 'credits-1',
    component: 'credits',
    title: 'Credits',
    position: { referencePanel: chat, direction: 'within' },
    inactive: true,
  });
  chart.api.setActive();
}

const prefersLight = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: light)').matches;

type SaveState = 'loading' | 'saved' | 'saving' | 'error';

export function WorkstationPage() {
  const [dock, setDock] = useState<DockviewApi | null>(null);
  const [saved, setSaved] = useState<SaveState>('loading');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const restoring = useRef(false);
  const qc = useQueryClient();

  const save = useCallback(async (d: DockviewApi) => {
    try {
      await api.saveLayout(LAYOUT, d.toJSON() as unknown as Record<string, unknown>);
      setSaved('saved');
    } catch {
      setSaved('error');
    }
  }, []);

  /** Debounced: 800 ms after the last layout change (drag, resize, add/remove, params). */
  const schedule = useCallback(
    (d: DockviewApi) => {
      if (restoring.current) return;
      if (timer.current) clearTimeout(timer.current);
      setSaved('saving');
      timer.current = setTimeout(() => void save(d), 800);
    },
    [save],
  );

  const onReady = useCallback(
    async (event: DockviewReadyEvent) => {
      const d = event.api;
      setDock(d);
      restoring.current = true;
      try {
        const { layout } = await api.layout(LAYOUT);
        if (layout) {
          try {
            d.fromJSON(layout as unknown as SerializedDockview);
          } catch {
            defaultLayout(d);
          }
        } else defaultLayout(d);
        setSaved('saved');
      } catch {
        defaultLayout(d);
        setSaved('error');
      } finally {
        restoring.current = false;
      }
      d.onDidLayoutChange(() => schedule(d));
    },
    [schedule],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const add = (kind: TileKind) => {
    if (!dock) return;
    const active = dock.activePanel;
    dock.addPanel({
      id: nextId(dock, kind),
      component: kind,
      title: TILES[kind].title,
      ...(kind === 'chart' ? { params: { coin: 'BTC', interval: '1h' } } : {}),
      ...(kind === 'app'
        ? { params: { tool: 'get_chart', args: { coin: 'ETH', interval: '1h' } } }
        : {}),
      ...(active ? { position: { referencePanel: active, direction: 'within' } } : {}),
    });
  };

  // Widgets the agent (or the user) created appear as tiles automatically: poll the list and add any
  // not yet in the layout, next to the active panel. Closing the tile only removes it from the layout.
  const widgets = useQuery({
    queryKey: ['widgets'],
    queryFn: api.widgets,
    refetchInterval: 10_000,
  });
  const placed = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!dock || !widgets.data || saved === 'loading') return;
    for (const w of widgets.data.widgets) {
      const id = `widget-${w.id}`;
      if (dock.getPanel(id) || placed.current.has(id)) continue;
      placed.current.add(id);
      const active = dock.activePanel;
      dock.addPanel({
        id,
        component: 'widget',
        title: w.title,
        params: { widgetId: w.id },
        ...(active ? { position: { referencePanel: active, direction: 'within' } } : {}),
      });
    }
  }, [dock, widgets.data, saved]);

  const reset = async () => {
    if (!dock) return;
    restoring.current = true;
    try {
      defaultLayout(dock);
    } finally {
      restoring.current = false;
    }
    await save(dock);
    qc.invalidateQueries({ queryKey: ['ws'] });
  };

  const status =
    saved === 'loading'
      ? 'loading layout…'
      : saved === 'saving'
        ? 'saving…'
        : saved === 'error'
          ? 'layout not saved'
          : 'layout saved';
  return (
    <div className="workstation">
      <div className="ws-toolbar">
        <span className="muted">Add tile:</span>
        {(Object.keys(TILES) as TileKind[]).map((k) => (
          <button key={k} type="button" className="small" onClick={() => add(k)} disabled={!dock}>
            + {TILES[k].title}
          </button>
        ))}
        <span className="grow" />
        <span className="muted" data-testid="layout-status">
          {status}
        </span>
        <button type="button" className="small" onClick={reset} disabled={!dock}>
          Reset layout
        </button>
      </div>
      <div className="ws-dock">
        <DockviewReact
          components={components}
          onReady={onReady}
          theme={prefersLight() ? themeLight : themeDark}
        />
      </div>
    </div>
  );
}
